import React, { useEffect } from "react";
import { useStoreState } from "pullstate";

import { UIStore } from "@/store/UIStore";
import { apiPath } from "@/helpers/apiPath";
import client from "@/helpers/apiClient";
import { defaultPageItems } from "@/helpers/apiPathHelpers";
import { CommentsInterface } from "@/helpers/pageHelpers/Common/interface";
import SimpleSection from "@/components/Section/SimpleSection";
import CommentInput from "@/components/Comments/CommentInput";
import CommentBox from "@/components/Comments/CommentBox";
import Typography from "@/components/Typography";
import Button from "@/components/Button";
import { usePagination } from "@/helpers/usePagination";
import { useDataLayer } from "@/helpers/useDataLayer";

import styles from "./Comments.module.scss";

export const Comments: React.FC<CommentsInterface> = ({
  slug,
  type,
  contentData,
  subtags,
}) => {
  const { userLoggedIn, commentsCount } = useStoreState(
    UIStore,
    (store) => store
  );
  const { pushToDataLayer } = useDataLayer();
  const {
    mutate,
    isEmpty,
    elements: comments,
    isReachingEnd,
    isLoadingInitialData,
    loadMore,
  } = usePagination([], defaultPageItems.COMMENTS, (index) =>
    apiPath[`${type}_COMMENTS`]({
      slug,
      page: index + 1,
      perPage: defaultPageItems.COMMENTS,
    })
  );

  useEffect(() => {
    mutate();
  }, [userLoggedIn]);

  const isCommentable = userLoggedIn;
  const postComment = async (message) => {
    try {
      await client.post(apiPath[`POST_${type}_COMMENT`]({ slug }), { message });
    } catch ({ error }) {
    } finally {
      await mutate();
    }
    if (contentData) {
      const objectToSend = {
        title: contentData.title,
        author: contentData.authors?.map(({ name }) => name),
        type: type,
        tag: contentData.tags.map(({ title }) => title),
        subtag: subtags?.map(({ title }) => title),
      };

      pushToDataLayer("comment", objectToSend);
    }
  };

  const removeComment = async (id: number) => {
    try {
      await client.delete(apiPath[`REMOVE_${type}_COMMENT`]({ slug, id }));
    } catch ({ error }) {
      console.warn(error);
    } finally {
      await mutate();
    }
  };

  return (
    <SimpleSection>
      <div className={styles.comments}>
        <div className={styles.actionHeader}>
          <Typography
            variant="h3"
            component="h5"
            className={styles.commentsHeading}
          >
            Komentarze{" "}
            <span className={styles.commentsCount}>{commentsCount}</span>
          </Typography>
        </div>

        {isCommentable && <CommentInput sendComment={postComment} />}

        {!isEmpty && (
          <div className={styles.commentsWrapper}>
            {comments.map((comment, index) => (
              <CommentBox
                key={index}
                removeComment={removeComment}
                {...comment}
              />
            ))}
          </div>
        )}

        {!isReachingEnd && !isLoadingInitialData && (
          <Button
            variant="secondary"
            onClick={loadMore}
            className={styles.loadMore}
          >
            Więcej komentarzy
          </Button>
        )}
      </div>
    </SimpleSection>
  );
};

export default Comments;
